import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { colors } from '@guryou/colors';
import { Block, ContainedButton, FlexRow, Form, LargeText, PaperBlock, TextAreaBox, TextBox } from '@guryou/html-components';
import { Box } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import clsx from 'clsx';
import ModernDropdown from '../components/ModernDropdown';

const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 50px)',
    marginTop: '50px',
    padding: theme.spacing(3),
    backgroundColor: '#212121',
    color: 'white',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));

const CircuitFormView = ({ open }) => {
  const classes = useStyles();
  const history = useHistory();
  const [circuit, setCircuit] = useState({
    circuit_type: 'training',
    activity_type: 'gym',
    execution_mode: 'repetition',
    execution_display_units: 'times',
  });
  const [circuitExercises, setCircuitExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);

  useEffect(() => {
    const fetchExercises = async () => {
      const query = `
          query Exercises {
            exercises {
              id
              name
            }
          }
        `;
      try {
        const response = await fetch('https://1ccbrgd0eh.execute-api.eu-west-1.amazonaws.com/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query }),
        });
        const result = await response.json();
        if (result.data && result.data.exercises) {
          setAllExercises(result.data.exercises.map(ex => ({ label: ex.name, value: ex.id })));
        }
      } catch (error) {
        console.error('Error fetching exercises:', error);
      }
    };
    fetchExercises();
  }, []);

  const circuitTypeOptions = [
    { value: 'warmup', label: 'Warmup' },
    { value: 'training', label: 'Training' },
    { value: 'cool_down', label: 'Cool Down' },
  ];

  const activityTypeOptions = [
    { value: 'gym', label: 'Gym' },
    { value: 'bodyweight', label: 'Bodyweight' },
    { value: 'callisthenics', label: 'Callisthenics' },
    { value: 'run', label: 'Run' },
    { value: 'trailRun', label: 'Trail Run' },
    { value: 'swim', label: 'Swim' },
    { value: 'bike', label: 'Bike' },
  ];

  const executionModeOptions = [
    { value: 'repetition', label: 'Repetition' },
    { value: 'timed', label: 'Timed' },
    { value: 'emom', label: 'EMOM' },
    { value: 'amrap', label: 'AMRAP' },
    { value: 'tabata', label: 'Tabata' },
    { value: 'superset', label: 'Superset' },
    { value: 'triset', label: 'Triset' },
  ];

  const executionDisplayUnitsOptions = [
    { value: 'times', label: 'Times' },
    { value: 'min', label: 'Minutes' },
    { value: 'sec', label: 'Seconds' },
    { value: 'meters', label: 'Meters' },
    { value: 'km', label: 'Kilometers' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'pounds', label: 'Pounds' },
    { value: 'percent', label: 'Percent' },
    { value: 'kcal', label: 'kCal' },
    { value: 'hours', label: 'Hours' },
  ];

  const exerciseExecModeOptions = [
    { value: 'repetition', label: 'Repetition' },
    { value: 'repetitionPerSide', label: 'Repetition Per Side' },
    { value: 'duration', label: 'Duration' },
    { value: 'durationPerSide', label: 'Duration Per Side' },
    { value: 'distance', label: 'Distance' },
    { value: 'laps', label: 'Laps' },
    { value: 'repetitionMaximum', label: 'Repetition Maximum' },
    { value: 'pyramid', label: 'Pyramid' },
  ];

  const weightModeOptions = [
    { value: 'KG', label: 'kg' },
    { value: 'KG_per_side', label: 'kg per side' },
    { value: 'RPE', label: 'RPE' },
    { value: 'one_RM', label: '1RM %' },
    { value: 'Buffer', label: 'Buffer' },
  ];

  const getFloatValue = val => {
    if (val === null || val === undefined || val === '') {
      return null;
    }
    const num = parseFloat(val);
    return isNaN(num) ? null : num;
  };
  const getIntValue = val => {
    if (val === null || val === undefined || val === '') {
      return null;
    }
    const num = parseInt(val, 10);
    return isNaN(num) ? null : num;
  };

  const handleSubmit = async data => {
    try {
      const circuitInput = {
        title: data.title,
        description: data.description || '',
        provider_id: 260,
        circuit_type: circuit.circuit_type,
        activity_type: circuit.activity_type,
        target_muscles: data.target_muscles,
        execution_mode: circuit.execution_mode,
        circuit_rest_time: data.circuit_rest_time ? parseInt(data.circuit_rest_time, 10) : 0,
        execution_display_units: circuit.execution_display_units,
      };

      const createCircuitQuery = `
        mutation CreateCircuitDefinition($input: CircuitDefinitionInput!) {
          createCircuitDefinition(input: $input) {
            id
          }
        }
      `;
      const createCircuitVariables = {
        input: circuitInput,
      };

      const response = await fetch('https://1ccbrgd0eh.execute-api.eu-west-1.amazonaws.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: createCircuitQuery,
          variables: createCircuitVariables,
        }),
      });

      const result = await response.json();

      if (result.errors) {
        console.error('Error creating circuit:', result.errors);
        alert(`Error creating circuit. Please try again.`);
      } else {
        console.log(`Circuit created successfully:`, result.data);
        const circuitId = result.data.createCircuitDefinition.id;

        for (const exercise of circuitExercises) {
          const exerciseInput = {
            circuit_id: circuitId,
            exercise_id: exercise.exercise_id,
            execution_mode: exercise.execution_mode,
            execution_display_units: exercise.execution_display_units,
            execution_max_value: getFloatValue(exercise.execution_max_value),
            execution_min_value: getFloatValue(exercise.execution_min_value),
            use_weight: exercise.use_weight,
            weight: getFloatValue(exercise.weight),
            weight_mode: exercise.weight_mode,
            rest_time: getIntValue(exercise.rest_time),
            set_series: getIntValue(exercise.set_series),
          };

          const createExerciseQuery = `
            mutation CreateCircuitExercise($input: CircuitExerciseInput!) {
              createCircuitExercise(input: $input) {
                id
              }
            }
          `;

          try {
            const exerciseResponse = await fetch('https://1ccbrgd0eh.execute-api.eu-west-1.amazonaws.com/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: createExerciseQuery,
                variables: { input: exerciseInput },
              }),
            });
            const exerciseResult = await exerciseResponse.json();
            if (exerciseResult.errors) {
              console.error('Error creating circuit exercise:', exerciseResult.errors);
              alert(`Error creating exercise for circuit.`);
            }
          } catch (error) {
            console.error('Error creating circuit exercise:', error);
            alert(`Error creating exercise for circuit.`);
          }
        }

        history.goBack();
      }
    } catch (error) {
      console.error('Error creating circuit:', error);
      alert(`Error creating circuit. Please try again.`);
    }
  };

  const addExercise = () => {
    setCircuitExercises(prev => [
      ...prev,
      {
        id: Date.now(),
        exercise_id: null,
        execution_mode: 'repetition',
        execution_display_units: 'times',
        use_weight: false,
        weight_mode: 'KG',
      },
    ]);
  };

  const removeExercise = index => {
    setCircuitExercises(prev => prev.filter((_, i) => i !== index));
  };

  const handleExerciseChange = (index, field, value) => {
    setCircuitExercises(prev => {
      const newExercises = [...prev];
      newExercises[index][field] = value;
      return newExercises;
    });
  };

  return (
    <main
      className={clsx(classes.content, {
        [classes.contentShift]: open,
      })}
    >
      <Box style={{ overflowY: 'auto', flex: 1 }}>
        <IconButton onClick={() => history.goBack()} style={{ marginBottom: '16px' }}>
          <ChevronLeftIcon style={{ color: 'white' }} />
        </IconButton>

        <LargeText fontWeight="700" width="fit-content" margin="0 auto" marginBottom={10} color={colors.white}>
          Create Circuit
        </LargeText>

        <Form actions={false} onSubmit={handleSubmit} parserVersion={2}>
          <FlexRow alignItems="flex-start" style={{ marginBottom: '40px' }}>
            <div style={{ flex: 1, marginRight: '8px' }}>
              <TextBox
                name="title"
                label="Title"
                required={true}
                InputLabelProps={{ style: { color: 'white' } }}
                inputProps={{ style: { color: 'white' } }}
              />
            </div>
            <div style={{ flex: 1, marginLeft: '8px' }}>
              <TextAreaBox
                label="Description"
                name="description"
                withToolbar={false}
                InputLabelProps={{ style: { color: 'white' } }}
                inputProps={{ style: { color: 'white' } }}
              />
            </div>
          </FlexRow>

          <FlexRow alignItems="center" style={{ marginBottom: '40px' }}>
            <div style={{ flex: 1, marginRight: '8px' }}>
              <ModernDropdown
                label="Circuit Type"
                options={circuitTypeOptions}
                value={circuit.circuit_type}
                onChange={value => setCircuit(c => ({ ...c, circuit_type: value }))}
              />
            </div>
            <div style={{ flex: 1, marginLeft: '8px', marginRight: '8px' }}>
              <ModernDropdown
                label="Activity Type"
                options={activityTypeOptions}
                value={circuit.activity_type}
                onChange={value => setCircuit(c => ({ ...c, activity_type: value }))}
              />
            </div>
            <div style={{ flex: 1, marginLeft: '8px' }}>
              <ModernDropdown
                label="Execution Mode"
                options={executionModeOptions}
                value={circuit.execution_mode}
                onChange={value => setCircuit(c => ({ ...c, execution_mode: value }))}
              />
            </div>
          </FlexRow>

          <FlexRow alignItems="flex-start" style={{ marginBottom: '40px' }}>
            <div style={{ flex: 1, marginRight: '8px' }}>
              <TextBox
                name="target_muscles"
                label="Target Muscles"
                InputLabelProps={{ style: { color: 'white' } }}
                inputProps={{ style: { color: 'white' } }}
              />
            </div>
            <div style={{ flex: 1, marginLeft: '8px', marginRight: '8px' }}>
              <TextBox
                name="circuit_rest_time"
                label="Circuit Rest Time (seconds)"
                type="number"
                InputLabelProps={{ style: { color: 'white' } }}
                inputProps={{ style: { color: 'white' } }}
              />
            </div>
            <div style={{ flex: 1, marginLeft: '8px' }}>
              <ModernDropdown
                label="Execution Display Units"
                options={executionDisplayUnitsOptions}
                value={circuit.execution_display_units}
                onChange={value => setCircuit(c => ({ ...c, execution_display_units: value }))}
              />
            </div>
          </FlexRow>

          <Block>
            <LargeText color={colors.white}>Exercises</LargeText>
            {circuitExercises.map((exercise, index) => (
              <PaperBlock key={exercise.id} margin="10px 0" padding="20px" backgroundColor="#333">
                <FlexRow justifyContent="space-between" alignItems="center">
                  <LargeText> Exercise {index + 1} </LargeText>
                  <ContainedButton text="Remove" onClick={() => removeExercise(index)} />
                </FlexRow>
                <FlexRow alignItems="center" style={{ margin: '20px 0' }}>
                  <div style={{ flex: 1, marginRight: '8px' }}>
                    <ModernDropdown
                      label="Exercise"
                      options={allExercises}
                      value={exercise.exercise_id}
                      onChange={value => handleExerciseChange(index, 'exercise_id', value)}
                    />
                  </div>
                  <div style={{ flex: 1, marginLeft: '8px' }}>
                    <ModernDropdown
                      label="Execution Mode"
                      options={exerciseExecModeOptions}
                      value={exercise.execution_mode}
                      onChange={value => handleExerciseChange(index, 'execution_mode', value)}
                    />
                  </div>
                </FlexRow>
                <FlexRow alignItems="center" style={{ margin: '20px 0' }}>
                  <div style={{ flex: 1, marginRight: '8px' }}>
                    <ModernDropdown
                      label="Execution Display Units"
                      options={executionDisplayUnitsOptions}
                      value={exercise.execution_display_units}
                      onChange={value => handleExerciseChange(index, 'execution_display_units', value)}
                    />
                  </div>
                  <div style={{ flex: 1, marginLeft: '8px', marginRight: '8px' }}>
                    <TextBox
                      name="execution_min_value"
                      label="Min Executions"
                      type="number"
                      value={exercise.execution_min_value}
                      onChange={e => handleExerciseChange(index, 'execution_min_value', e.target.value)}
                      InputLabelProps={{ style: { color: 'white' } }}
                      inputProps={{ style: { color: 'white' } }}
                    />
                  </div>
                  <div style={{ flex: 1, marginLeft: '8px' }}>
                    <TextBox
                      name="execution_max_value"
                      label="Max Executions"
                      type="number"
                      value={exercise.execution_max_value}
                      onChange={e => handleExerciseChange(index, 'execution_max_value', e.target.value)}
                      InputLabelProps={{ style: { color: 'white' } }}
                      inputProps={{ style: { color: 'white' } }}
                    />
                  </div>
                </FlexRow>
                <FlexRow alignItems="center" style={{ margin: '20px 0' }}>
                  <div style={{ flex: 1, marginRight: '8px' }}>
                    <TextBox
                      name="rest_time"
                      label="Rest Time (seconds)"
                      type="number"
                      value={exercise.rest_time}
                      onChange={e => handleExerciseChange(index, 'rest_time', e.target.value)}
                      InputLabelProps={{ style: { color: 'white' } }}
                      inputProps={{ style: { color: 'white' } }}
                    />
                  </div>
                  <div style={{ flex: 1, marginLeft: '8px' }}>
                    <TextBox
                      name="set_series"
                      label="Set Series"
                      type="number"
                      value={exercise.set_series}
                      onChange={e => handleExerciseChange(index, 'set_series', e.target.value)}
                      InputLabelProps={{ style: { color: 'white' } }}
                      inputProps={{ style: { color: 'white' } }}
                    />
                  </div>
                </FlexRow>
                <FlexRow alignItems="center" style={{ margin: '20px 0' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="use_weight"
                        color="primary"
                        checked={exercise.use_weight}
                        onChange={e => handleExerciseChange(index, 'use_weight', e.target.checked)}
                        style={{ color: 'white' }}
                      />
                    }
                    label="Use Weights"
                  />
                  {exercise.use_weight && (
                    <>
                      <div style={{ flex: 1, marginRight: '8px' }}>
                        <TextBox
                          name="weight"
                          label="Weight"
                          type="number"
                          value={exercise.weight}
                          onChange={e => handleExerciseChange(index, 'weight', e.target.value)}
                          InputLabelProps={{ style: { color: 'white' } }}
                          inputProps={{ style: { color: 'white' } }}
                        />
                      </div>
                      <div style={{ flex: 1, marginLeft: '8px' }}>
                        <ModernDropdown
                          label="Weight Mode"
                          options={weightModeOptions}
                          value={exercise.weight_mode}
                          onChange={value => handleExerciseChange(index, 'weight_mode', value)}
                        />
                      </div>
                    </>
                  )}
                </FlexRow>
              </PaperBlock>
            ))}
            <ContainedButton onClick={addExercise} text="Add Exercise" />
          </Block>

          <Block textAlign="center" marginTop={4} marginBottom={4}>
            <ContainedButton type="submit" text="Create" />
          </Block>
        </Form>
      </Box>
    </main>
  );
};

export default CircuitFormView;
